import type {
  AdminAttemptAnswer,
  AdminKnowledgeCheck,
  AdminKnowledgeCheckAttempt,
  AdminKnowledgeCheckAttemptListItem,
  AnswerDecision,
  AnswerVerdict,
  ReviewAttemptRequest,
  ReviewVerdict,
} from "@/redux/certifications/types";
import type { DynamicValueResponse } from "@/redux/datablocks/types";
import type {
  ConfirmedScore,
  OrderedAnswer,
  ReviewBlocker,
  ReviewDraft,
} from "@/features/certifications/knowledge-check-flags/types";
import {
  ANSWER_VERDICTS,
  PASSING_RULES,
} from "@/domains/certifications/constants";
import { REVIEW_BLOCKERS } from "@/features/certifications/knowledge-check-flags/constants";

export const toReviewVerdict = (
  verdict: AnswerVerdict | "",
): ReviewVerdict | null =>
  verdict === ANSWER_VERDICTS.CORRECT || verdict === ANSWER_VERDICTS.INCORRECT
    ? verdict
    : null;

export const hasAiVerdicts = (answers: AdminAttemptAnswer[]) =>
  answers.some((answer) => toReviewVerdict(answer.aiVerdict) !== null);

export const countAiFlagged = (answers: AdminAttemptAnswer[]) =>
  answers.filter((answer) => answer.aiVerdict !== ANSWER_VERDICTS.CORRECT)
    .length;

export const createDraft = (answers: AdminAttemptAnswer[]): ReviewDraft => ({
  verdicts: Object.fromEntries(
    answers.map((answer) => [answer.field, toReviewVerdict(answer.aiVerdict)]),
  ),
  feedback: Object.fromEntries(answers.map((answer) => [answer.field, ""])),
});

export const createDecidedDraft = (
  attempt: AdminKnowledgeCheckAttempt,
): ReviewDraft => ({
  verdicts: Object.fromEntries(
    attempt.answers.map((answer) => [
      answer.field,
      toReviewVerdict(answer.verdict),
    ]),
  ),
  feedback: Object.fromEntries(
    attempt.answers.map((answer) => [answer.field, answer.feedback]),
  ),
});

export const getConfirmedScore = (
  answers: AdminAttemptAnswer[],
  draft: ReviewDraft,
): ConfirmedScore => {
  const total = answers.length;
  const correct = answers.filter(
    (answer) => draft.verdicts[answer.field] === ANSWER_VERDICTS.CORRECT,
  ).length;
  return {
    correct,
    total,
    percent: total > 0 ? Math.round((correct / total) * 100) : 0,
  };
};

export const isPreviewedPass = (
  { correct, total }: ConfirmedScore,
  check?: Pick<AdminKnowledgeCheck, "passingRule" | "passingCount">,
) => {
  if (
    check?.passingRule === PASSING_RULES.AT_LEAST_N &&
    check.passingCount !== null
  ) {
    return correct >= check.passingCount;
  }
  return total > 0 && correct === total;
};

export const getReviewBlocker = (
  answers: AdminAttemptAnswer[],
  draft: ReviewDraft,
): ReviewBlocker | null => {
  if (answers.some((answer) => !draft.verdicts[answer.field])) {
    return REVIEW_BLOCKERS.VERDICTS;
  }
  const isMissingFeedback = answers.some(
    (answer) =>
      draft.verdicts[answer.field] === ANSWER_VERDICTS.INCORRECT &&
      !draft.feedback[answer.field]?.trim(),
  );
  return isMissingFeedback ? REVIEW_BLOCKERS.FEEDBACK : null;
};

export const buildReviewRequest = (
  attempt: AdminKnowledgeCheckAttempt,
  draft: ReviewDraft,
): ReviewAttemptRequest | null => {
  const answers: AnswerDecision[] = [];
  for (const answer of attempt.answers) {
    const verdict = draft.verdicts[answer.field];
    if (!verdict) {
      return null;
    }
    answers.push({
      fieldId: answer.field,
      verdict,
      feedback: draft.feedback[answer.field]?.trim() ?? "",
    });
  }
  return {
    attemptId: attempt.id,
    answers,
  };
};

export const formatAnswerValue = (value: DynamicValueResponse): string => {
  if (value === null || value === undefined) {
    return "";
  }
  if (Array.isArray(value)) {
    return value
      .map((entry) => (typeof entry === "object" ? entry.choice : entry))
      .join(", ");
  }
  if (typeof value === "object") {
    return String(value.choice);
  }
  return String(value);
};

export const sortAttempts = (attempts: AdminKnowledgeCheckAttemptListItem[]) =>
  [...attempts].sort((a, b) => a.attemptNumber - b.attemptNumber);

export const orderAnswers = (
  answers: AdminAttemptAnswer[],
  check: Pick<AdminKnowledgeCheck, "questions"> | undefined,
): OrderedAnswer[] => {
  const fields = check
    ? [...check.questions.templateFields].sort((a, b) => a.order - b.order)
    : [];
  const placeOf = (field: number) => {
    const index = fields.findIndex(({ id }) => id === field);
    return index === -1 ? fields.length : index;
  };
  return [...answers]
    .map((answer, index) => ({ answer, index, place: placeOf(answer.field) }))
    .sort((a, b) => a.place - b.place || a.index - b.index)
    .map(({ answer }, index) => ({
      answer,
      position: index + 1,
      title: fields.find(({ id }) => id === answer.field)?.title ?? null,
    }));
};
